import { useEffect, useRef } from 'react';

/**
 * Custom hook for handling inactivity timeout
 * @param {number} timeoutMinutes - Timeout duration in minutes
 * @param {Function} onTimeout - Callback function to execute when timeout occurs
 * @param {Array} resetEvents - Array of event types that should reset the timer (default: ['keydown', 'mousedown', 'mousemove', 'click', 'scroll', 'touchstart'])
 */
export const useInactivityTimeout = (timeoutMinutes, onTimeout, resetEvents = ['keydown', 'mousedown', 'mousemove', 'click', 'scroll', 'touchstart']) => {
  const timeoutRef = useRef(null);
  const timeoutSetRef = useRef(false);

  const resetTimer = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      if (onTimeout && typeof onTimeout === 'function') {
        onTimeout();
      }
    }, timeoutMinutes * 60 * 1000);
  };

  const startTimeout = () => {
    if (!timeoutSetRef.current) {
      timeoutSetRef.current = true;
      resetTimer();
    }
  };

  const stopTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    timeoutSetRef.current = false;
  };

  useEffect(() => {
    // Add event listeners for reset events
    const eventHandlers = resetEvents.map(eventType => {
      const handler = () => {
        if (timeoutSetRef.current) {
          resetTimer();
        }
      };
      document.addEventListener(eventType, handler);
      return { eventType, handler };
    });

    // Cleanup function
    return () => {
      stopTimeout();
      eventHandlers.forEach(({ eventType, handler }) => {
        document.removeEventListener(eventType, handler);
      });
    };
  }, [timeoutMinutes, onTimeout, ...resetEvents]);

  return {
    startTimeout,
    stopTimeout,
    resetTimer,
    isActive: timeoutSetRef.current
  };
}; 